<script lang="ts" setup>
import { GpBrandMessage, GpBtn, GpCheckbox, useFaIcons } from '@gportal/uikit';
import AddSshKeysModal from '@panel/components/user/ssh-keys/AddSshKeysModal.vue';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import type { ListProjectSSHKeysResponse } from 'gpcore-grpc/gpcore/api/cloud/v1/responses_pb';
import type { SSHKey } from 'gpcore-grpc/gpcore/type/v1/ssh_key_pb';
import { computed, onMounted, ref, watch } from 'vue';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

interface User {
  name: string;
  email: string;
  username: string;
  keys: Array<SSHKey>;
}

const props = defineProps<{ isActive: boolean }>();

const emit = defineEmits<{ (e: 'keysChange', value: Array<string>) }>();

useFaIcons({ faPlus });

const users = ref<Record<string, User>>({});
const selected = ref<Array<string>>([]);

const isModalOpen = ref(false);

const isUserSelected = (id: string) => {
  const user = users.value[id];
  if (user) {
    if (user.keys.length === 0) return false;

    for (const key of user.keys) {
      if (!selected.value.includes(key.id)) {
        return false;
      }
    }
  }

  return true;
};
const selectUser = (id: string) => {
  const user = users.value[id];

  if (user) {
    if (isUserSelected(id)) {
      user.keys.forEach((key) => {
        selected.value = selected.value.filter((s: string) => s !== key.id);
      });
    } else {
      user.keys.forEach((key) => {
        if (!selected.value.includes(key.id)) {
          selected.value.push(key.id);
        }
      });
    }
  }

  emit('keysChange', selected.value);
};

const isKeySelected = (id: string) => selected.value.includes(id);
const select = (id: string) => {
  if (isKeySelected(id)) {
    selected.value = selected.value.filter((s: string) => s !== id);
  } else {
    selected.value.push(id);
  }

  emit('keysChange', selected.value);
};

const projectStore = useProjectStore();
const sessionStore = useSessionStore();

const session = computed(() => sessionStore.session);
const preSelectCurrentUserKeys = () => {
  if (!isUserSelected(session.value.user.id)) selectUser(session.value.user.id);
};
const reloadKeys = () =>
  projectStore.reloadProjectSSHKeys().then((keys: ListProjectSSHKeysResponse) => {
    const reloadedUsers = {};
    keys.keys.forEach((key) => {
      const userId = key.user!.id;
      if (reloadedUsers[userId] == null) {
        reloadedUsers[userId] = {
          name: key.user!.fullName,
          email: key.user!.email,
          username: key.user!.username,
          keys: new Array<SSHKey>(),
        };
      }

      const user = reloadedUsers[userId] as User;
      if (user) {
        user.keys.push(key.sshKey!);
      }

      reloadedUsers[userId] = user;

      users.value = reloadedUsers;
    });
  });

const handleModalClose = () => {
  isModalOpen.value = false;
};

const handleKeyCreated = async (sshKey: SSHKey) => {
  if (sshKey != null) {
    isModalOpen.value = false;
  }
  await reloadKeys();
  select(sshKey.id);
};

watch(
  () => props.isActive,
  () => {
    if (props.isActive && selected.value.length === 0) {
      preSelectCurrentUserKeys();
      return;
    }

    if (!props.isActive) {
      selected.value = [];
    }
  },
);

onMounted(async () => {
  await reloadKeys();
  preSelectCurrentUserKeys();
});
</script>
<template>
  <div>
    <AddSshKeysModal :show="isModalOpen" @close="handleModalClose" @created="handleKeyCreated" />

    <template v-if="Object.keys(users).length > 0">
      <div class="tw-mb-2 tw-overflow-auto">
        <template v-for="(user, key) in users" :key="user.name">
          <div class="tw-mb-2 tw-flex">
            <GpCheckbox
              class="tw-mr-4"
              :input-id="`sshkey__${user.name}`"
              :model-value="isUserSelected(key)"
              @update:model-value="selectUser(key)"
            />
            <span class="tw-font-bold">{{ user.name || user.email || user.username }}</span>
          </div>
          <ul class="tw-ml-4">
            <li
              v-for="(key, index) in user.keys"
              :key="key.id"
              class="tw-mb-2 tw-flex tw-items-center tw-overflow-auto"
              v-playwright="`nodes-create-ssh-keys-${index}`"
            >
              <GpCheckbox
                class="tw-mr-4"
                :input-id="`sshkey__${key.id}`"
                :model-value="isKeySelected(key.id)"
                @update:model-value="select(key.id)"
              />
              <p class="tw-mb-4">
                <span class="tw-font-bold">{{ key.name }}</span>
                <br />
                <span class="tw-text-xs">{{ key.fingerprint }}</span>
              </p>
            </li>
          </ul>
        </template>
      </div>
      <p v-if="isActive && selected.length === 0" class="tw-mb-4 tw-text-gp-crab">
        No keys selected.
      </p>
    </template>

    <GpBrandMessage v-else class="tw-mx-auto tw-mb-4" message="No SSH public keys found." />

    <GpBtn
      class="tw-w-full"
      outline
      @click="isModalOpen = true"
      label="Add Key"
      :icon="['fas', 'fa-plus']"
      v-playwright="'nodes-create-ssh-keys-add'"
    />
  </div>
</template>
